import React, { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Upload as UploadIcon, Loader2, FileVideo, X } from "lucide-react";
import { useApi } from "../api/useApi";
import { uploadFile } from "../api/resources";
import {
  subscriptionLevelForEmail,
  maxUploadDurationSec,
  canSelectPremium,
  defaultAnalysisMode,
  AnalysisMode,
} from "../subscription";
import ExtractionStep from "../components/ExtractionStep";
import { uploadExtractionDecision } from "../pose/captureCadence";
import { FOOTBALL_UPLOAD_OPTIONS, FOOTBALL_GROUP, FOOTBALL_EMOJI } from "../constants/football";

// The option shape and the picker itself live in the shared component, so the upload screen and
// the session page's "change technique" render one identical list.
import SportPicker, { SportOption } from "../components/SportPicker";

// SportId values come from EleetzSportTechniqueSelect.tsx (iOS) — same ids land on the backend.
// Exported so the session page can offer the same techniques when correcting a mis-picked one
// (owner, 2026-09-20: "I uploaded a deadlift as a squat technique"). One list, so a technique
// that can be uploaded can also be corrected to, and neither view can drift from the other.
export const SPORTS: SportOption[] = [
  // The last techniques the backend could analyse but nobody could select. Golf chipping tracks the
  // club, football tracks the ball; both are GPU-only attachments, so a free upload of those still
  // analyses with the implement's own cues unavailable. Workplace ergonomics is body pose only.
  { id: 6,  label: "Golf Chipping — Face On",       group: "Golf", emoji: "⛳", available: true },
  { id: 7,  label: "Golf Chipping — Down the Line", group: "Golf", emoji: "⛳", available: true },
  // Workplace ergonomics — body pose only, so these run free on the device like any other.
  { id: 25, label: "Workplace — Lifting",          group: "Workplace", emoji: "📦", available: true },
  { id: 26, label: "Workplace — Push / Pull",      group: "Workplace", emoji: "📦", available: true },
  { id: 27, label: "Workplace — Overhead Work",    group: "Workplace", emoji: "📦", available: true },
  { id: 28, label: "Workplace — Repetitive Task",  group: "Workplace", emoji: "📦", available: true },
  { id: 29, label: "Workplace — Ladder Use",       group: "Workplace", emoji: "🪜", available: true },
  { id: 1,  label: "Golf swing — Face On",     group: "Golf",    emoji: "🏌️", available: true },
  { id: 3,  label: "Golf swing — Down the Line", group: "Golf",  emoji: "🏌️", available: true },
  { id: 4,  label: "Putting — Face On",         group: "Golf",    emoji: "⛳",  available: true },
  { id: 5,  label: "Putting — Down the Line",   group: "Golf",    emoji: "⛳",  available: true },
  { id: 8,  label: "Serve — Face On",           group: "Tennis",  emoji: "🎾", available: true },
  { id: 2,  label: "Serve — Down the Line",     group: "Tennis",  emoji: "🎾", available: true },
  { id: 9,  label: "Serve — Front View",        group: "Tennis",  emoji: "🎾", available: true },
  { id: 10, label: "Forehand — Face On",        group: "Tennis",  emoji: "🎾", available: true },
  { id: 11, label: "Forehand — Down the Line",  group: "Tennis",  emoji: "🎾", available: true },
  { id: 12, label: "Forehand — Front View",     group: "Tennis",  emoji: "🎾", available: true },
  { id: 13, label: "Backhand — Face On",        group: "Tennis",  emoji: "🎾", available: true },
  { id: 14, label: "Backhand — Down the Line",  group: "Tennis",  emoji: "🎾", available: true },
  { id: 15, label: "Backhand — Front View",     group: "Tennis",  emoji: "🎾", available: true },
  { id: 20, label: "Bowling — Side View",       group: "Bowling", emoji: "🎳", available: true },
  { id: 21, label: "Bowling — Down the Line",   group: "Bowling", emoji: "🎳", available: true },
  ...FOOTBALL_UPLOAD_OPTIONS.map((o) => ({ id: o.id, label: o.label, group: FOOTBALL_GROUP, emoji: FOOTBALL_EMOJI, available: true })),
  { id: 22, label: "Run Gait — Side View",      group: "Movement", emoji: "🏃", available: true },
  { id: 62, label: "Volleyball Serve — Side View",  group: "Volleyball", emoji: "🏐", available: true },
  { id: 63, label: "Volleyball Serve — Front View", group: "Volleyball", emoji: "🏐", available: true },
  { id: 64, label: "Baseball Pitch — Side View",    group: "Baseball", emoji: "⚾", available: true },
  { id: 65, label: "Baseball Pitch — Front View",   group: "Baseball", emoji: "⚾", available: true },
  { id: 69, label: "Deadlift — Side View",          group: "Movement", emoji: "🏋️", available: true },
  { id: 70, label: "Deadlift — Front View",         group: "Movement", emoji: "🏋️", available: true },
  { id: 36, label: "Skate — Ollie",                 group: "Skateboarding", emoji: "🛹", available: true },
  { id: 37, label: "Skate — Kickflip",              group: "Skateboarding", emoji: "🛹", available: true },
  { id: 38, label: "Skate — Drop-in",               group: "Skateboarding", emoji: "🛹", available: true },
  { id: 16, label: "Cricket Batting — Front View",  group: "Cricket", emoji: "🏏", available: true },
  { id: 17, label: "Cricket Batting — Side View",   group: "Cricket", emoji: "🏏", available: true },
  { id: 42, label: "Warrior II — Front View",   group: "Yoga",    emoji: "🧘", available: true },
  { id: 43, label: "Warrior II — Side View",    group: "Yoga",    emoji: "🧘", available: true },
  { id: 24, label: "Squat — Side View",         group: "Movement", emoji: "🏋️", available: true },
  { id: 44, label: "Squat — Front View",        group: "Movement", emoji: "🏋️", available: true },
  { id: 45, label: "Squat — Behind View",       group: "Movement", emoji: "🏋️", available: true },
  { id: 71, label: "Lunge — Side View",         group: "Movement", emoji: "🏋️", available: true },
  { id: 72, label: "Lunge — Front View",        group: "Movement", emoji: "🏋️", available: true },
  { id: 66, label: "Push-Up — Side View",       group: "Movement", emoji: "💪", available: true },
  { id: 67, label: "Push-Up — Front View",      group: "Movement", emoji: "💪", available: true },
  { id: 23, label: "Vertical Jump — Side View",  group: "Movement", emoji: "🤾", available: true },
  { id: 48, label: "Vertical Jump — Front View", group: "Movement", emoji: "🤾", available: true },
  { id: 75, label: "Burpee — Side View",         group: "Movement", emoji: "🔥", available: true },
  { id: 46, label: "Downward Dog — Side View",  group: "Yoga",    emoji: "🧘", available: true },
  { id: 47, label: "Downward Dog — Front View", group: "Yoga",    emoji: "🧘", available: true },
  { id: 58, label: "Cobra — Side View",         group: "Yoga",    emoji: "🧘", available: true },
  { id: 57, label: "Jab-Cross — Side View",      group: "Boxing",  emoji: "🥊", available: true },
  { id: 68, label: "Jab-Cross — Front View",     group: "Boxing",  emoji: "🥊", available: true },
  { id: 55, label: "Plank — Side View",         group: "Yoga",    emoji: "🧘", available: true },
  { id: 53, label: "Chair — Side View",         group: "Yoga",    emoji: "🧘", available: true },
  { id: 54, label: "Chair — Front View",        group: "Yoga",    emoji: "🧘", available: true },
  { id: 73, label: "Pull-up — Front View",      group: "Movement", emoji: "🧗", available: true },
  { id: 74, label: "Pull-up — Side View",       group: "Movement", emoji: "🧗", available: true },
  { id: 60, label: "Triangle — Front View",     group: "Yoga",    emoji: "🧘", available: true },
  { id: 61, label: "Triangle — Side View",      group: "Yoga",    emoji: "🧘", available: true },
  { id: 59, label: "Bridge — Side View",        group: "Yoga",    emoji: "🧘", available: true },
  { id: 49, label: "Tree Pose — Front View",    group: "Yoga",    emoji: "🧘", available: true },
  { id: 50, label: "Tree Pose — Side View",     group: "Yoga",    emoji: "🧘", available: true },
  { id: 31, label: "Savage — Front View",       group: "Dance",   emoji: "🕺", available: true },
  { id: 32, label: "Levitating — Front View",   group: "Dance",   emoji: "💃", available: true },
  { id: -1, label: "Dance (coming soon)",       group: "Dance",   emoji: "💃", available: false },
  { id: 30, label: "Renegade — Front View",    group: "Dance",   emoji: "💃", available: true },
  { id: 33, label: "Toosie Slide — Front View", group: "Dance",   emoji: "🕺", available: true },
  { id: 51, label: "Free Throw — Side View",    group: "Basketball", emoji: "🏀", available: true },
  { id: 52, label: "Free Throw — Front View",   group: "Basketball", emoji: "🏀", available: true },
  { id: 18, label: "Bowling — DTL",             group: "Cricket", emoji: "🏏", available: true },
  { id: 19, label: "Bowling — Front View",      group: "Cricket", emoji: "🏏", available: true },
  { id: 35, label: "WAP — Front View",          group: "Dance",   emoji: "🕺", available: true },
  { id: 34, label: "Out West — Front View",    group: "Dance",   emoji: "💃", available: true },
];

const ACCEPTED_TYPES = "video/mp4,video/quicktime,video/x-m4v,video/*";

export default function Upload() {
  const navigate = useNavigate();
  const apiFetch = useApi();
  const { user } = useAuth0();
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Longer clips cost ~linearly more to split/label and tend to contain multiple swings (which
  // hurts analysis), so the public cap stays low; the policy lives in subscription.ts per level.
  const subscriptionLevel = subscriptionLevelForEmail(user?.email);
  const maxDurationSec = maxUploadDurationSec(subscriptionLevel);
  // On-device pose extraction runs for every upload (it is the free analysis's input and the
  // premium run's shadow labels). The only choice is WHICH analysis, and only once entitled.
  const [analysisMode, setAnalysisMode] = useState<AnalysisMode>(defaultAnalysisMode(subscriptionLevel));
  const [keypointsJson, setKeypointsJson] = useState<string | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [duration, setDuration] = useState<number | null>(null);
  const [sportId, setSportId] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const acceptFile = useCallback((f: File | null) => {
    if (!f) return;
    if (!f.type.startsWith("video/")) {
      setError("That doesn't look like a video file.");
      return;
    }
    setError(null);
    setFile(f);
    setDuration(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(f));
  }, [previewUrl]);

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    acceptFile(e.target.files?.[0] ?? null);
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    acceptFile(e.dataTransfer.files?.[0] ?? null);
  };

  const clearFile = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl(null);
    setDuration(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const onSubmit = async () => {
    if (!file || sportId == null || duration == null) return;
    if (duration > maxDurationSec) {
      setError(`Video is ${Math.round(duration)}s — please trim to under ${maxDurationSec}s.`);
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const res = await uploadFile(apiFetch, {
        file,
        sportId,
        videoDurationSec: duration,
        // Nullable by contract: only attached when on-device extraction ran to completion.
        keypointsJson: keypointsJson ?? undefined,
        analysisMode,
      });
      const ids = res.newRecordIds ?? res.NewRecordIds ?? [];
      // HandleUploadFile pushes SFC id first, then file-attach id, then SO id, then label id.
      // The sfcId is what the rest of the app calls uploadId.
      const uploadId = ids[0];
      if (uploadId == null) {
        throw new Error("Upload succeeded but no upload id was returned.");
      }
      navigate(`/sessions/${uploadId}`);
    } catch (e: any) {
      setError(e?.message ?? "Upload failed. Please try again.");
      setUploading(false);
    }
  };

  const canSubmit =
    !!file && sportId != null && duration != null && duration <= maxDurationSec && !uploading;

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <div>
        <h1 className="text-2xl font-semibold">Upload a video</h1>
        <p className="text-sm text-slate-500 mt-1">
          Drop a short clip (under {maxDurationSec}s) and pick the sport. We'll analyze it and
          send you back overlays, joint angles, and feedback.
        </p>
      </div>

      {/* Drop zone / preview */}
      {!file ? (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          className={`cursor-pointer rounded-2xl border-2 border-dashed p-10 text-center transition
            ${dragOver ? "border-orange-500 bg-orange-50" : "border-slate-300 hover:border-slate-400 bg-slate-50"}`}
        >
          <UploadIcon className="h-10 w-10 mx-auto text-slate-400" />
          <p className="mt-3 text-sm font-medium text-slate-700">
            Drop a video here, or click to choose a file
          </p>
          <p className="mt-1 text-xs text-slate-500">MP4, MOV — up to {maxDurationSec} seconds</p>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_TYPES}
            className="hidden"
            onChange={onPick}
          />
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-200 overflow-hidden bg-black">
          <div className="flex items-center justify-between px-3 py-2 bg-slate-900 text-slate-100 text-sm">
            <span className="flex items-center gap-2 truncate">
              <FileVideo className="h-4 w-4" />
              <span className="truncate">{file.name}</span>
              {duration != null && (
                <span className="text-slate-400 ml-2">{Math.round(duration)}s</span>
              )}
            </span>
            <button
              type="button"
              onClick={clearFile}
              className="text-slate-400 hover:text-white"
              aria-label="Remove file"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {/* Hidden metadata loader only — the clip is shown ONCE, in the extraction step's
              MediaPipe canvas below, never as a second video player. The duration read here
              gates that step. */}
          {previewUrl && (
            <video
              src={previewUrl}
              className="hidden"
              muted
              playsInline
              preload="metadata"
              onLoadedMetadata={(e) => {
                const v = e.currentTarget;
                if (isFinite(v.duration)) setDuration(v.duration);
              }}
            />
          )}
        </div>
      )}

      {/* Analysis choice (freemium). Free users see WHAT they get and what Premium adds — the
          choice stays locked until the paid tier is purchasable. Entitled users pick, Premium
          preselected. Either way on-device extraction runs (below). */}
      {file && previewUrl && (
        <div className="space-y-1.5" data-testid="analysis-tier-choice">
          <div className="text-sm font-medium text-slate-700">Analysis</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label
              className={
                "block cursor-pointer rounded-md border px-3 py-2 " +
                (analysisMode === "free" ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-slate-50")
              }
            >
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                {canSelectPremium(subscriptionLevel) && (
                  <input
                    type="radio"
                    name="analysisMode"
                    value="free"
                    checked={analysisMode === "free"}
                    onChange={() => setAnalysisMode("free")}
                    className="accent-emerald-600"
                  />
                )}
                Free
              </div>
              <div className="text-xs text-slate-600">
                Pose extracted on this device · scored feedback on your key frames
              </div>
            </label>
            <label
              className={
                "block rounded-md border px-3 py-2 " +
                (canSelectPremium(subscriptionLevel) ? "cursor-pointer " : "opacity-70 ") +
                (analysisMode === "premium" ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-slate-50")
              }
            >
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                {canSelectPremium(subscriptionLevel) && (
                  <input
                    type="radio"
                    name="analysisMode"
                    value="premium"
                    checked={analysisMode === "premium"}
                    onChange={() => setAnalysisMode("premium")}
                    className="accent-emerald-600"
                  />
                )}
                Premium
                {!canSelectPremium(subscriptionLevel) && (
                  <span className="text-emerald-700">· coming soon</span>
                )}
              </div>
              <div className="text-xs text-slate-600">
                GPU analysis (club + ball tracking) · narrated feedback video
              </div>
            </label>
          </div>
        </div>
      )}

      {/* Sport selector */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Sport & angle</label>
        <SportPicker
          options={SPORTS}
          selectedId={sportId}
          onSelect={(id) => {
            setSportId(id);
            // A movement technique's pose comes from the split frames, not from an on-device
            // extraction paired by index: drop anything already extracted for a fixed-cadence pick.
            if (uploadExtractionDecision(id) !== "extract") setKeypointsJson(null);
          }}
        />
      </div>

      {/* On-device pose extraction (web-pose-extraction step 4). The SPORT decides the cadence
          (NetCoreInterfaces.CaptureCadence): fixed-cadence sports extract here, at the split's
          10 fps, so the pose file pairs with the split frames by index; movement sports are split at
          the clip's native rate, which only the splitter knows, so their frames are labelled
          after the split instead — hence the sport picker sits above this slot. */}
      {file && previewUrl && duration != null && (() => {
        const decision = uploadExtractionDecision(sportId);
        if (decision === "extract")
          return <ExtractionStep previewUrl={previewUrl} durationSec={duration} onPoseFile={setKeypointsJson} />;
        if (decision === "afterSplit")
          return (
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600" data-testid="extraction-after-split">
              Movement clips are analysed at the video's own frame rate: the pose is extracted from
              the frames after upload, on this device, so nothing to do here.
            </div>
          );
        return (
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500" data-testid="extraction-awaits-sport">
            Choose the sport and angle above to start the on-device pose extraction.
          </div>
        );
      })()}

      {/* Errors */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Submit */}
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="btn-secondary"
          disabled={uploading}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={!canSubmit}
          className={`btn-primary ${!canSubmit ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          {uploading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Uploading…
            </>
          ) : (
            <>
              <UploadIcon className="h-4 w-4" /> Upload &amp; analyze
            </>
          )}
        </button>
      </div>
    </div>
  );
}
